import React, { useEffect, useState } from "react";
import { userCanContribute } from "../../utils/userRoles";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useProductGuide } from "./GuideContext";
import {
  formatCphProcessingEta,
  isCphSampleGuide,
  isGuidePrimarySatisfied,
  resolveGuideStripTip,
} from "./guideProgress";

/**
 * Compact coach: tip + CTAs. No long body.
 * Primary CTA hides when the user is already on the target screen.
 */
export default function GuideStrip() {
  const guide = useProductGuide();
  const { isMobile } = useDeviceDetection();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const canContribute = (() => {
    try {
      return userCanContribute(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {
      return false;
    }
  })();

  const showCphEta =
    Boolean(guide?.active) &&
    guide?.chapter?.id === "processing" &&
    isCphSampleGuide(guide?.script);

  useEffect(() => {
    if (!showCphEta) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [showCphEta, guide?.state?.processingStartedAt]);

  if (!guide || !canContribute) return null;
  if (!guide.active) return null;
  if (!guide.chapter) return null;

  const {
    chapter,
    primaryAction,
    secondaryAction,
    skipGuide,
    offPath,
    focusNudge,
    focusActive,
    resumeLabel,
    currentRoute,
    homePanel,
  } = guide;

  const cphEtaLabel = showCphEta
    ? formatCphProcessingEta(guide.state?.processingStartedAt, nowMs)
    : null;

  const wizardUi = guide.wizardUi || null;
  const sampleNotesLoaded = Boolean(
    guide.state?.sampleNotesLoaded || wizardUi?.hasNotes
  );
  // Already on Add knowledge: never show a blinking strip CTA like
  // "Back to Add knowledge" — the in-page button (Create / Continue / Extract)
  // is the next action. Only show strip resume when truly off-route.
  const onAddKnowledgeRoute =
    String(currentRoute || "") === "add-knowledge" ||
    (String(currentRoute || "") === "home" &&
      String(homePanel || "") === "add");
  // Don't treat a blocked-nav nudge as "off path" while already on Add knowledge.
  const softFocusCue = !onAddKnowledgeRoute && (offPath || focusNudge);
  const primarySatisfied = isGuidePrimarySatisfied(chapter.id, {
    currentRoute: currentRoute || guide.state?.currentRoute,
    homePanel: homePanel ?? guide.homePanel,
    sampleNotesLoaded,
    wizardStep: wizardUi?.step,
  });
  const stepCue = resolveGuideStripTip(chapter, {
    currentRoute: currentRoute || "",
    homePanel: homePanel ?? "",
    isMobile,
    offPath: softFocusCue,
    sampleNotesLoaded,
    wizardUi,
    entityId: guide.state?.entityId || "",
  });
  const inAddWizardChapter = ["create-app", "choose-how", "supply"].includes(
    chapter.id
  );
  // Only suppress strip CTAs while the Add-knowledge wizard owns the next
  // click. After job-queued (processing+), Open Inbox must stay visible even
  // if wizardUi is still stale on "source".
  const hideStripPrimaryForWizard =
    inAddWizardChapter &&
    (wizardUi?.step === "anchor" ||
      wizardUi?.step === "pick" ||
      wizardUi?.step === "source" ||
      wizardUi?.step === "scope" ||
      onAddKnowledgeRoute);
  const primaryLabel = softFocusCue
    ? resumeLabel || "Continue guide"
    : chapter.primaryLabel;
  const showPrimary =
    Boolean(primaryLabel) &&
    !hideStripPrimaryForWizard &&
    (softFocusCue || (Boolean(chapter.primaryAction) && !primarySatisfied));
  const showSecondary =
    !softFocusCue &&
    Boolean(chapter.secondaryLabel && chapter.secondaryAction);

  const lockNote = focusActive
    ? "Guided demo — other parts of the app stay locked until this step is done."
    : null;

  return (
    <div
      className="border-t border-emerald-200/80 bg-emerald-50/95 text-emerald-950"
      role="complementary"
      aria-label="Product guide"
    >
      <div
        className={
          isMobile
            ? "flex flex-col gap-1 px-3 py-1.5"
            : "flex flex-col gap-1 px-4 py-1.5 sm:px-6"
        }
      >
        {lockNote ? (
          <p className="text-[11px] font-medium leading-snug text-emerald-800/85">
            {lockNote}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {stepCue ? (
            <p
              className={
                softFocusCue
                  ? "min-w-0 flex-1 text-xs font-medium leading-snug text-amber-950/90"
                  : "min-w-0 flex-1 text-xs font-medium leading-snug text-emerald-950/90"
              }
            >
              {stepCue}
              {cphEtaLabel ? (
                <span className="mt-0.5 block font-semibold tabular-nums text-emerald-900/80 sm:ml-2 sm:mt-0 sm:inline">
                  · Typical ~2–3 min · {cphEtaLabel}
                </span>
              ) : null}
            </p>
          ) : (
            <span className="min-w-0 flex-1" />
          )}
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {showSecondary ? (
              <button
                type="button"
                onClick={() => secondaryAction()}
                className="rounded-md border border-emerald-200/90 bg-white/80 px-2.5 py-1 text-xs font-medium text-emerald-950 hover:bg-white"
              >
                {chapter.secondaryLabel}
              </button>
            ) : null}
            {showPrimary ? (
              <button
                key={`guide-cta-${chapter.id}-${softFocusCue ? "resume" : "primary"}`}
                type="button"
                onClick={() => primaryAction()}
                className="guide-cta-attention rounded-md bg-emerald-800 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-900"
              >
                {primaryLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => skipGuide()}
              className="rounded-md px-2 py-1 text-xs font-medium text-emerald-900/70 hover:bg-emerald-100/80"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
